<template>
  <define-benefits>
    <ul v-if="benefits" class="f-col gap-4 lg:flex-row lg:gap-8 lg:wrap">
      <li v-for="({ label, thumbnail }) in benefits.data.slice(0, 10)" :key="label" class="flex items-center gap-4 lg:max-w-64">
        <span class="flex shrink-0 center">
          <base-picture
            v-if="thumbnail"
            alt=""
            fit="contain"
            height="50"
            :src="thumbnail"
            width="50"
          />
        </span>
        <span class="line-clamp-3 ws-normal overline-1 fw-bold overflow-wrap-break-word">
          {{ label }}
        </span>
      </li>
    </ul>
  </define-benefits>

  <define-benefit-rating>
    <div v-if="benefitRating">
      <h2 :class="$feature.showProductDetailsOnPDP ? 'mb-6 title-3' : 'mb-8 title-2'">
        {{ benefitRating.label }}
      </h2>
      <lazy-product-accordions-scale
        :data="benefitRating.data"
        :title="benefitRating.label"
      />
    </div>
  </define-benefit-rating>

  <div v-if="$feature.showProductDetailsOnPDP" class="space-y-8">
    <div>
      <h2 v-if="description" class="mb-6 title-3">
        {{ description.label }}
      </h2>
      <div class="grid gap-8">
        <p v-if="description" class="body-2 lg:max-w-140">
          {{ description.data[0]?.text }}
        </p>
        <reuse-benefits />
      </div>
    </div>

    <reuse-benefit-rating />

    <div>
      <vf-accordion
        v-for="section in orderedSections"
        :key="section.id"
        :id="section.id"
        icon="plus"
        icon-collapse="minus"
        :title="section.label"
        variant="aligned"
        @close="onAccordionInteraction(section.label ?? section.id, false)"
        @expand="onAccordionInteraction(section.label ?? section.id, true)"
      >
        <div
          class="pb-8 pt-2"
          :class="section.sections?.length ? 'body-2 c-black' : 'text-sm c-grey-20'"
        >
          <component
            :is="componentTypes[resolveComponentType(section.id, section.layout, section.sections)]"
            :data="section.data"
            :sections="section.sections"
            :title="section.label"
          />
        </div>
      </vf-accordion>
    </div>
  </div>

  <div v-else class="space-y-8">
    <div>
      <h2 v-if="description" class="mb-6 title-2">
        {{ description.label }}
      </h2>
      <div class="grid gap-8">
        <div v-if="description">
          <div class="line-clamp-2 body-2 lg:max-w-140">
            {{ description.data[0]?.text }}
          </div>
          <base-button
            class="mt-2 text-base c-brand-1 fw-bold underlined"
            @click="PanelProductDetails.open({ productDetails })"
          >
            {{ $t.readMore }}
          </base-button>
        </div>
        <reuse-benefits />
      </div>
    </div>

    <reuse-benefit-rating />

    <vf-button class="<md:full" variant="secondary" @click="PanelProductDetails.open({ productDetails })">
      {{ $t.productDetails }}
    </vf-button>
    <panel-product-details />
  </div>
</template>

<script lang="ts" setup>
import type { ProductDetailWithSections } from '#types/product'
import {
  LazyProductAccordionsBulletin,
  LazyProductAccordionsDetailCard,
  LazyProductAccordionsFeaturesExtended,
  LazyProductAccordionsGrid,
  LazyProductAccordionsKeyValue,
  LazyProductAccordionsModalGrid,
  LazyProductAccordionsScale,
} from '#components'

export type Props = {
  productDetails: ProductDetailWithSections[]
}
const { productDetails } = defineProps<Props>()
const { $gtm } = useNuxtApp()

const description = computed(() => productDetails.find((detail) => detail.id === 'productText'))
const benefits = computed(() => productDetails.find((detail) => detail.id === 'Benefits'))
const benefitRating = computed(() => productDetails.find((detail) => detail.id === 'benefitRatings'))
const PanelProductDetails = createPanel('product-details')

const componentTypes = {
  bulletin: LazyProductAccordionsBulletin,
  detailCard: LazyProductAccordionsDetailCard,
  featuresExtended: LazyProductAccordionsFeaturesExtended,
  grid: LazyProductAccordionsGrid,
  keyValue: LazyProductAccordionsKeyValue,
  modalGrid: LazyProductAccordionsModalGrid,
  scale: LazyProductAccordionsScale
}

type ComponentType = keyof typeof componentTypes
const isComponentType = (value?: string): value is ComponentType =>
  !!value && Object.hasOwn(componentTypes, value)

const resolveComponentType = (id: string, layout?: string, sections?: ProductDetailWithSections['sections']): ComponentType => {
  if (sections?.length && isComponentType(id)) return id
  return isComponentType(layout) ? layout : 'detailCard'
}

// productText, Benefits rendered separately above; benefitRatings shown as scale
const EXCLUDED_IDS = ['productText', 'Benefits', 'benefitRatings']

const SECTION_ORDER = [
  'featuresExtended',
  'featuresBulletin',
  'productFeatures',
  'featuresKeyValue',
  'productDescription',
  'productDetails',
  'benefitRatings',
  'benefitsAttribute',
  'careInstructions'
] as const

// CMS may return legacy IDs that differ from the canonical SECTION_ORDER keys
const ID_ALIASES: Record<string, typeof SECTION_ORDER[number]> = {
  features: 'productFeatures',
  Details: 'productDetails',
  description: 'productDescription',
}

const orderedSections = computed(() => {
  const filtered = productDetails.filter(({ id }) => !EXCLUDED_IDS.includes(id))
  const getOrderId = ({ id }: ProductDetailWithSections) => ID_ALIASES[id] ?? id

  const ordered = SECTION_ORDER
    .map((id) => filtered.find((s) => getOrderId(s) === id))
    .filter(Boolean) as ProductDetailWithSections[]

  const rest = filtered.filter((s) => !(SECTION_ORDER as readonly string[]).includes(getOrderId(s)))

  return [...ordered, ...rest]
})

const onAccordionInteraction = createAccordionInteractionHandler($gtm)

const [DefineBenefits, ReuseBenefits] = createReusableTemplate()
const [DefineBenefitRating, ReuseBenefitRating] = createReusableTemplate()
</script>
